import { useState, useEffect } from "react";
import { APITypesV1 as APITypes } from "@cur8/api-client";
import { makeStyles } from "@material-ui/styles";
import { IconButton, Tooltip } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { LargeCanvasImage } from "./LargeCanvasImage";
import { ZoomedCanvasImage } from "./ZoomedCanvasImage";
import { DUIImage } from "./DUIImage";
import { useAPIClient } from "render/context/APIContext";

const useStyles = makeStyles({
  Carousel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  Controls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    marginBottom: "10px",
  },
  ImageLabel: {
    fontSize: "16px",
    minWidth: "200px",
    textAlign: "center",
  },
  ImageWrapper: {
    flex: 1,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    touchAction: "pan-y pinch-zoom",
  },
  SlideContainer: {
    display: "flex",
    width: "100%",
    height: "100%",
    transition: "transform 0.3s ease-out",
    willChange: "transform",
  },
  Slide: {
    minWidth: "100%",
    maxWidth: "100%",
    flex: "0 0 100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
});

interface ImageCarouselProps {
  annotation: APITypes.Annotation;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

type ImageType = "overview" | "zoomed" | "derma";

export function ImageCarousel({
  annotation,
  onFullscreenChange,
}: ImageCarouselProps) {
  const classes = useStyles();
  const api = useAPIClient();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [availableImages, setAvailableImages] = useState<ImageType[]>([]);
  const [dermaAnnotation, setDermaAnnotation] =
    useState<APITypes.Annotation | null>(null);
  const [rigAnnotation, setRigAnnotation] =
    useState<APITypes.Annotation | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullscreenChange = (fullscreen: boolean) => {
    setIsFullscreen(fullscreen);
    onFullscreenChange?.(fullscreen);
  };

  const isRigImage = annotation.targetUri.startsWith("cur8:recording");

  // Minimum swipe distance (in pixels) to trigger a navigation
  const minSwipeDistance = 50;

  useEffect(() => {
    async function checkForRigAndDermaImages() {
      const images: ImageType[] = [];
      let rigAnnotation: APITypes.Annotation | null = null;

      if (isRigImage) {
        // For rig images, set the rig annotation and always have overview and zoomed
        setRigAnnotation(annotation);
        images.push("overview", "zoomed");
      } else {
        // For derma images, check if there's a linked rig annotation
        setDermaAnnotation(annotation);

        try {
          // The derma annotation's targetUri points to another annotation (the rig annotation)
          if (annotation.targetUri.includes(":annotation:")) {
            const tokens = annotation.targetUri.split(":");
            const externalId = tokens[2];
            const rigAnnotationId = tokens[4]; // The annotation ID from the targetUri

            // Fetch the rig annotation directly by ID
            const rigAnno = await api.annotation.getAnnotation({
              patientId: externalId,
              annotationId: rigAnnotationId,
            }).result;

            // Check if this is actually a rig/panorama annotation
            if (
              rigAnno.targetUri.startsWith("cur8:recording") ||
              rigAnno.applicationSpecificTarget?.startsWith("panorama:") ||
              rigAnno.applicationSpecificTarget?.startsWith("rgbino:")
            ) {
              rigAnnotation = rigAnno;
              // Add overview and zoomed for the rig annotation
              images.push("overview", "zoomed");
            }
          }
        } catch (error) {
          console.error("Error checking for rig annotation:", error);
        }

        // Always add derma image
        images.push("derma");
      }

      setAvailableImages(images);

      // Store the rig annotation for rendering if we found one
      if (rigAnnotation) {
        setRigAnnotation(rigAnnotation);
      }
    }

    checkForRigAndDermaImages();
  }, [annotation, isRigImage, api]);

  const handlePrevious = () => {
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : availableImages.length - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev < availableImages.length - 1 ? prev + 1 : 0
    );
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (isFullscreen) return;

    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (isFullscreen || !touchStart) return;

    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);

    // Calculate drag offset for visual feedback
    const offset = currentTouch - touchStart;
    setDragOffset(offset);
  };

  const onTouchEnd = () => {
    if (isFullscreen) return;

    setIsDragging(false);
    setDragOffset(0);

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };
  const getImageLabel = (type: ImageType): string => {
    switch (type) {
      case "overview":
        return "Overview (Full Panorama)";
      case "zoomed":
        return "Zoomed (Lesion Detail)";
      case "derma":
        return "Derma Image";
      default:
        return "";
    }
  };

  const renderImage = (imageType: ImageType) => {
    switch (imageType) {
      case "overview":
        return rigAnnotation ? (
          <LargeCanvasImage
            annotation={rigAnnotation}
            onFullscreenChange={handleFullscreenChange}
          />
        ) : (
          <div>No rig annotation available</div>
        );
      case "zoomed":
        return rigAnnotation ? (
          <ZoomedCanvasImage anno={rigAnnotation} />
        ) : (
          <div>No rig annotation available</div>
        );
      case "derma":
        return dermaAnnotation ? (
          <DUIImage anno={dermaAnnotation} isOverview={false} />
        ) : (
          <div>No derma image available</div>
        );
      default:
        return null;
    }
  };

  const getTransform = () => {
    if (availableImages.length === 0) return "translateX(0)";

    const baseOffset = -currentIndex * 100;
    const dragPercentage = isDragging
      ? (dragOffset / window.innerWidth) * 100
      : 0;

    return `translateX(${baseOffset + dragPercentage}%)`;
  };

  if (availableImages.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <div className={classes.Carousel}>
      {!isFullscreen && (
        <div className={classes.Controls}>
          <IconButton
            color="primary"
            size="large"
            onClick={handlePrevious}
            disabled={availableImages.length <= 1}
          >
            <Tooltip title="Previous image">
              <ArrowBack fontSize="inherit" />
            </Tooltip>
          </IconButton>
          <div className={classes.ImageLabel}>
            {getImageLabel(availableImages[currentIndex])} ({currentIndex + 1}/
            {availableImages.length})
          </div>
          <IconButton
            color="primary"
            size="large"
            onClick={handleNext}
            disabled={availableImages.length <= 1}
          >
            <Tooltip title="Next image">
              <ArrowForward fontSize="inherit" />
            </Tooltip>
          </IconButton>
        </div>
      )}
      <div
        className={classes.ImageWrapper}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {availableImages.length === 0 ? (
          <div>Loading...</div>
        ) : (
          <div
            className={classes.SlideContainer}
            style={{
              transform: getTransform(),
              transition: isDragging ? "none" : "transform 0.3s ease-out",
            }}
          >
            {availableImages.map((imageType, index) => (
              <div key={index} className={classes.Slide}>
                {renderImage(imageType)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
